import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const ACTIONS = {
  "employee.create": "added employee", "employee.invite": "invited", "employee.update": "edited employee", "employee.delete": "deleted employee",
  "employee.suspend": "suspended", "employee.restore": "restored", "employee.role": "changed role of",
  "card.update": "updated own card", "card.theme": "changed card colour", "card.photo": "uploaded photo",
  "card.photo_remove": "removed photo", "wallet.reset": "allowed new Wallet download for",
  "fields.update": "updated form fields", "fields.order": "reordered fields", "field.create": "added field",
  "field.update": "edited field", "field.delete": "deleted field", "role.create": "created role", "role.update": "edited role",
  "role.delete": "deleted role", "template.create": "created template", "template.update": "edited template",
  "template.delete": "deleted template", "organization.update": "updated organization settings",
  "translations.update": "updated translations", "password.set": "set backup password", "password.remove": "removed backup password",
  "wallet.configure": "set up Apple Wallet", "wallet.design": "changed Wallet design", "profile.design": "changed profile design", "profile.background": "uploaded a profile background", "profile.background_remove": "removed the profile background", "wallet.remove": "removed Apple Wallet certificate", "notifications.update": "updated notifications", "email.update": "updated email settings", "two_factor.on": "turned on two-factor for Admins",
  "two_factor.off": "turned off two-factor for Admins", "email.resend_on": "connected Resend", "email.resend_off": "disconnected Resend",
};
const DOT = { delete: "bg-wade", remove: "bg-wade", suspend: "bg-amber-500", create: "bg-emerald-500", restore: "bg-emerald-500" };
const dotFor = (action) => DOT[Object.keys(DOT).find((k) => action.includes(k))] || "bg-slate-300";

const Row = ({ it }) => {
  const { t, lang } = useLang();
  const at = new Date(it.at);
  return (
    <li className="flex items-baseline gap-3 px-4 py-2 text-sm hover:bg-slate-50" data-testid={`audit-row-${it.id}`}>
      <span className={`h-2 w-2 shrink-0 translate-y-[-1px] rounded-full ${dotFor(it.action)}`} />
      <time dir="ltr" className="w-28 shrink-0 font-mono text-xs text-slate-400" title={at.toLocaleString()}>
        {at.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", { day: "2-digit", month: "short" })} {at.toTimeString().slice(0, 5)}
      </time>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold text-slate-900">{it.actor_name}</span>{" "}
        <span className="text-slate-500">{t(ACTIONS[it.action] || it.action)}</span>{" "}
        {it.target && <span className="font-semibold text-slate-800">{it.target}</span>}
        {it.detail && <span className="ms-2 text-xs text-slate-400">· {it.detail}</span>}
      </span>
    </li>
  );
};

export const ActivityPanel = () => {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback((before = "") => {
    api.get("/admin/audit", { params: { q: query, before } }).then(({ data }) => {
      setItems((prev) => (before ? [...prev, ...data.items] : data.items));
      setHasMore(data.has_more);
    }).catch((e) => toast.error(errorMessage(e)));
  }, [query]);
  useEffect(() => { const id = setTimeout(() => load(), 250); return () => clearTimeout(id); }, [load]);

  return (
    <div className="space-y-3" data-testid="activity-panel">
      <div className="relative max-w-xs">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search people or changes")} data-testid="audit-search"
          className="h-10 w-full rounded-full border border-slate-200 bg-white pe-4 ps-9 text-sm outline-none focus:border-slate-900" />
      </div>
      {!items ? <div className="h-64 animate-pulse rounded-3xl bg-slate-200" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200/80 bg-white" data-testid="audit-list">
          {items.map((it) => <Row key={it.id} it={it} />)}
          {items.length === 0 && <li className="px-4 py-10 text-center text-sm text-slate-500" data-testid="audit-empty">{t("No activity yet.")}</li>}
        </ul>
      )}
      {hasMore && (
        <button onClick={() => load(items[items.length - 1].at)} data-testid="audit-load-more"
          className="btn-press min-h-[40px] rounded-full border border-slate-300 bg-white px-5 text-sm font-semibold hover:border-slate-900">{t("Load more")}</button>
      )}
    </div>
  );
};
